import type { Language } from "@/lib/translations";

export const deleteAccountCopy = {
  fr: {
    title: "Supprimer mon compte",
    subtitle:
      "Supprimez définitivement votre compte ReFound et toutes les données qui y sont associées.",
    login: {
      cardTitle: "Confirmez votre identité",
      cardDesc:
        "Pour protéger vos données, connectez-vous avec le même compte que dans l'application ReFound.",
      email: "Courriel",
      password: "Mot de passe",
      signIn: "Se connecter",
      signingIn: "Connexion…",
      or: "ou",
      google: "Continuer avec Google",
      apple: "Continuer avec Apple",
      invalidCredentials: "Courriel ou mot de passe incorrect.",
      genericError: "La connexion a échoué. Veuillez réessayer.",
    },
    confirm: {
      signedInAs: "Connecté en tant que",
      signOut: "Ce n'est pas vous? Se déconnecter",
      deletedTitle: "Ce qui sera supprimé définitivement",
      deletedItems: [
        "Votre compte et votre adresse courriel",
        "Tous vos reçus et les photos de reçus sauvegardées dans le nuage",
        "Vos garanties et rappels",
        "Vos partages de reçus et invitations",
        "Les surnoms de cartes et les 4 derniers chiffres de carte enregistrés",
        "Vos préférences, votre langue et vos jetons de notification",
        "Votre profil d'abonnement chez notre fournisseur de facturation (RevenueCat)",
      ],
      keptTitle: "Ce qui n'est pas supprimé",
      keptItems: [
        "Votre abonnement Premium, s'il est actif : il doit être annulé séparément dans les réglages de l'App Store (Apple) ou de Google Play. Supprimer le compte n'annule pas l'abonnement.",
        "Des données communautaires anonymes (ex. : échantillons de reçus non reconnus, sans aucun lien avec votre identité).",
      ],
      subscriptionWarning:
        "Important : supprimer votre compte n'annule PAS votre abonnement Premium. Annulez-le vous-même dans les réglages de l'App Store ou de Google Play avant ou après la suppression.",
      ack: "Je comprends que cette action est définitive et que mes données ne pourront pas être récupérées.",
      deleteButton: "Supprimer mon compte définitivement",
      dialogTitle: "Êtes-vous absolument certain?",
      dialogDesc:
        "Cette action est irréversible. Votre compte, vos reçus, vos photos et toutes vos données seront supprimés définitivement.",
      dialogCancel: "Annuler",
      dialogConfirm: "Oui, supprimer mon compte",
      deleting: "Suppression en cours… Ne fermez pas cette page.",
      deleteError:
        "La suppression a échoué. Rien n'a été supprimé de façon incorrecte — réessayez ou écrivez-nous à info@refoundapp.ca.",
    },
    done: {
      title: "Compte supprimé",
      desc: "Votre compte ReFound et toutes vos données ont été supprimés définitivement. Si une adresse courriel était associée à votre compte, un courriel de confirmation vous a été envoyé.",
      subscriptionReminder:
        "Rappel : si vous aviez un abonnement Premium, annulez-le dans les réglages de l'App Store ou de Google Play — la suppression du compte ne l'annule pas.",
      backHome: "Retour à l'accueil",
    },
  },
  en: {
    title: "Delete my account",
    subtitle: "Permanently delete your ReFound account and all data associated with it.",
    login: {
      cardTitle: "Confirm your identity",
      cardDesc:
        "To protect your data, sign in with the same account you use in the ReFound app.",
      email: "Email",
      password: "Password",
      signIn: "Sign in",
      signingIn: "Signing in…",
      or: "or",
      google: "Continue with Google",
      apple: "Continue with Apple",
      invalidCredentials: "Incorrect email or password.",
      genericError: "Sign-in failed. Please try again.",
    },
    confirm: {
      signedInAs: "Signed in as",
      signOut: "Not you? Sign out",
      deletedTitle: "What will be permanently deleted",
      deletedItems: [
        "Your account and email address",
        "All your receipts and receipt photos saved in the cloud",
        "Your warranties and reminders",
        "Your receipt shares and invitations",
        "Saved card nicknames and card last-4 digits",
        "Your preferences, language and notification tokens",
        "Your subscription profile at our billing provider (RevenueCat)",
      ],
      keptTitle: "What is not deleted",
      keptItems: [
        "Your Premium subscription, if active: it must be cancelled separately in your App Store (Apple) or Google Play settings. Deleting the account does not cancel the subscription.",
        "Anonymous community data (e.g. unrecognized receipt samples with no link to your identity).",
      ],
      subscriptionWarning:
        "Important: deleting your account does NOT cancel your Premium subscription. Cancel it yourself in your App Store or Google Play settings before or after deletion.",
      ack: "I understand this action is permanent and my data cannot be recovered.",
      deleteButton: "Permanently delete my account",
      dialogTitle: "Are you absolutely sure?",
      dialogDesc:
        "This action is irreversible. Your account, receipts, photos and all your data will be permanently deleted.",
      dialogCancel: "Cancel",
      dialogConfirm: "Yes, delete my account",
      deleting: "Deleting… Please don't close this page.",
      deleteError:
        "Deletion failed. Nothing was incorrectly deleted — try again or contact us at info@refoundapp.ca.",
    },
    done: {
      title: "Account deleted",
      desc: "Your ReFound account and all your data have been permanently deleted. If an email address was associated with your account, a confirmation email has been sent to you.",
      subscriptionReminder:
        "Reminder: if you had a Premium subscription, cancel it in your App Store or Google Play settings — deleting the account does not cancel it.",
      backHome: "Back to home",
    },
  },
} as const;

export type DeleteAccountCopy = (typeof deleteAccountCopy)[Language];
